import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PageHeader from '../../Common/PageHeader';
import Breadcrumbs from '../../Common/Breadcrumbs';
import TournamentCard from './components/TournamentCard';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { getUpcomingTournaments, getPastTournaments, Tournament as ApiTournament } from '../../api/clubmanager/tournamentService';
import './Tournaments.css';

// Use the API Tournament interface
type Tournament = ApiTournament;

const TournamentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upcoming'>('upcoming');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [filteredTournaments, setFilteredTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load tournaments based on active tab
    loadTournaments();
  }, [activeTab]);

  useEffect(() => {
    // Filter tournaments based on search and sort
    filterTournaments();
  }, [tournaments, searchTerm, sortBy]);

  const handleTabClick = (tabName: string) => {
    // Only handle upcoming since past tab is commented out
    setActiveTab('upcoming');
  };

  const loadTournaments = async () => {
    setLoading(true);
    setError(null);
    try {
      let tournamentData: Tournament[] = [];
      
      // Only load upcoming tournaments since past tab is commented out
      tournamentData = await getUpcomingTournaments();
      
      setTournaments(tournamentData);
      
      // Log the result for debugging
      console.log(`${activeTab} tournaments loaded:`, tournamentData.length);
    } catch (error: any) {
      console.error('Error loading tournaments:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to load tournaments';
      if (error.message) {
        if (error.message.includes('No authentication token found')) {
          errorMessage = 'Authentication required. Please log in again.';
        } else if (error.message.includes('API_BASE_URL not configured')) {
          errorMessage = 'Configuration error. Please contact support.';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (error.response?.status === 403) {
          errorMessage = 'Access denied. You do not have permission to view tournaments.';
        } else if (error.response?.status === 404) {
          // 404 errors are handled gracefully in the service, so this shouldn't happen
          errorMessage = 'Tournaments not found.';
        } else if (error.response?.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filterTournaments = () => {
    let filtered = [...tournaments];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(tournament =>
        tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tournament.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tournament.sports.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sort filter
    if (sortBy !== 'all') {
      filtered = filtered.filter(tournament => {
        if (sortBy === 'football') return tournament.sports === 'Football';
        if (sortBy === 'tennis') return tournament.sports === 'Tennis';
        if (sortBy === 'pickleball') return tournament.sports === 'Pickleball';
        return true;
      });
    }

    setFilteredTournaments(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleViewDetails = (tournamentId: string) => {
    navigate(`/tournaments/${tournamentId}`);
  };

  const handleRegisterNow = (tournamentId: string) => {
    navigate(`/tournaments/${tournamentId}/register`);
  };

  const getTabTitle = () => {
    // Only return upcoming tournaments title since past tab is commented out
    return 'Upcoming Tournaments';
  };

  const breadcrumbItems = [
    { label: 'Tournaments', onClick: () => navigate('/tournaments') },
    { label: `${getTabTitle()} Listing`, active: true }
  ];

  return (
    <div className="tournaments-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs - Left aligned like other modules */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Row>
          <Col>
            <div className="tournaments-container">
              {/* Main Tabs */}
              <Nav variant="tabs" className="tournament-tabs mb-4">
                <Nav.Item>
                  <Nav.Link 
                    className="tournament-tab"
                    active={activeTab === 'upcoming'}
                    onClick={() => handleTabClick('upcoming')}
                  >
                    Upcoming Tournaments
                  </Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                  <Nav.Link 
                    className="tournament-tab"
                    active={activeTab === 'past'}
                    onClick={() => handleTabClick('past')}
                  >
                    Past Tournaments
                  </Nav.Link>
                </Nav.Item> */}
              </Nav>

              {/* Tab Content Container */}
              <div className="tab-content-container">
                <>
                  {/* Search and Sort Controls */}
                  <div className="tournament-controls mb-4">
                    <Row className="align-items-center">
                      <Col md={6}>
                        <div className="tab-title-section">
                          <h3 className="tab-title">{getTabTitle()}</h3>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="filter-controls">
                          <Select
                            classNamePrefix="react-select"
                            value={{ value: sortBy, label: sortBy === 'all' ? 'All' : sortBy.charAt(0).toUpperCase() + sortBy.slice(1) }}
                            onChange={option => {
                              if (option) {
                                setSortBy(option.value);
                              }
                            }}
                            options={[
                              { value: 'all', label: 'All' },
                              { value: 'football', label: 'Football' },
                              { value: 'tennis', label: 'Tennis' },
                              { value: 'pickleball', label: 'Pickleball' }
                            ]}
                            placeholder="All"
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '40px', 
                                width: '160px',
                                marginRight: '12px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                boxShadow: 'none'
                              }),
                              menu: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            isSearchable={false}
                          />
                          <div className="search-input-wrapper">
                            <input
                              type="text"
                              className="form-search"
                              placeholder="Search tournaments"
                              value={searchTerm}
                              onChange={handleSearchChange}
                              style={{ width: '240px', paddingRight: '35px' }}
                            />
                            {searchTerm && (
                              <button
                                type="button"
                                className="clear-search-btn"
                                onClick={() => setSearchTerm('')}
                                title="Clear search"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {/* Error Alert */}
                  {error && (
                    <Alert variant="danger" className="mb-4">
                      <Alert.Heading>Error Loading Tournaments</Alert.Heading>
                      <p>{error}</p>
                      <hr />
                      <button 
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => loadTournaments()}
                      >
                        Try Again
                      </button>
                    </Alert>
                  )}

                  {/* Tournament Cards */}
                  {loading ? (
                    <div className="tournament-cards-row">
                      {[1, 2, 3, 4, 5, 6].map((index) => (
                        <div key={index} className="tournament-card-wrapper">
                          <ShimmerLoader type="tournament-card" />
                        </div>
                      ))}
                    </div>
                  ) : filteredTournaments.length > 0 ? (
                    <div className="tournament-cards-row">
                      {filteredTournaments.map((tournament, index) => (
                        <div key={tournament._id} className="tournament-card-wrapper">
                          <TournamentCard
                            tournament={tournament}
                            onViewDetails={() => handleViewDetails(tournament._id)}
                            onRegisterNow={() => handleRegisterNow(tournament._id)}
                            showRegisterButton={activeTab === 'upcoming'}
                            activeTab={activeTab}
                            index={index}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <h4 className="text-muted">No {activeTab} tournaments available</h4>
                      <p className="text-muted">
                        There are no {activeTab} tournaments at the moment.
                      </p>
                    </div>
                  )}
                </>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TournamentsPage; 